/**
 * SessionDoorPage — /sessions/:id/join
 *
 * The program-session sibling of EventDoorPage (registration events) — see
 * worker/src/session-door-routes.ts + Phase 4.A step 1 of the CC/EE event
 * unification (~/.claude/plans/phase4-live-cohort-migration.md). Deliberately
 * minimal — this is a doorway, not a landing page. Fetches the door payload
 * from GET /api/public/sessions/:id/door, resolves the visitor's identity
 * from a personal-link token (?at=) or device memory, and — inside the door
 * window — shows a one-tap "You're joining as Jane" confirm card. Tapping
 * records verified attendance (fire-and-forget) before opening the gated
 * join redirect. Anonymous visitors are asked for the email they registered
 * with (SessionDoorEmailCard) so verified attendance can still be recorded
 * without a token.
 *
 * FAIL-OPEN: if the door payload fetch itself fails, render a bare join
 * anchor to /zoom/sessions/:id — the server redirect works standalone and
 * enforces the real window regardless of what this page thinks the time is.
 *
 * No multi-day / replay-CTA branches here (unlike EventDoorPage) — a program
 * session is always single-occurrence, and post-session replay wiring is out
 * of scope for step 1 (Capture). This page is only ever reached when a
 * program has opted into attendance capture (programs.attendance_capture_from
 * set) — every other program's session links never route here.
 */

import { useEffect, useState, useRef, type FormEvent, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { suggestTypoCorrection } from './lib/email-validator';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const GET_HOST = 'https://get.thesuccessfulbookkeeper.com';

/** Mirrors EVENT_DOOR_OPEN_MS / EVENT_GRACE_AFTER_END_MS in worker/src/door-gate.ts. */
const DOOR_OPEN_MINUTES = 15;
const GRACE_MINUTES_AFTER_END = 180;

type DoorIdentity = { firstName: string | null; email: string };

/** Door email card — mirrors EventDoorEmailCard exactly (kept as a separate
 *  inline component rather than a shared extraction, same rationale as the
 *  event/call door pages: don't touch a proven page ahead of this one's own
 *  first live test). */
function SessionDoorEmailCard({ sessionId, joinUrl, joinBtnStyle, onRecognized }: {
  sessionId: string;
  joinUrl: string;
  joinBtnStyle: CSSProperties;
  onRecognized: (id: DoorIdentity) => void;
}) {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<'entry' | 'retry'>('entry');
  const [busy, setBusy] = useState(false);
  const suggestion = suggestTypoCorrection(email);

  const openZoom = () => window.open(joinUrl, '_blank', 'noopener,noreferrer');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean.includes('@')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/public/sessions/${encodeURIComponent(sessionId)}/checkin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean }),
      });
      const d = await r.json().catch(() => ({})) as { recognized?: boolean; firstName?: string | null };
      if (d.recognized) { onRecognized({ firstName: d.firstName ?? null, email: clean }); return; }
      setPhase('retry');
    } catch {
      openZoom();
    } finally {
      setBusy(false);
    }
  };

  const joinAsGuest = () => {
    const clean = email.trim().toLowerCase();
    if (clean.includes('@')) {
      fetch(`${API_BASE}/api/public/sessions/${encodeURIComponent(sessionId)}/checkin`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: clean, guest: true }), keepalive: true,
      }).catch(() => { /* the gate records; it never blocks entry */ });
    }
    openZoom();
  };

  const inputStyle: CSSProperties = {
    width: '100%', padding: '11px 13px', borderRadius: 8, fontSize: 14,
    border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)',
    color: '#fff', outline: 'none', boxSizing: 'border-box',
  };
  const linkStyle: CSSProperties = {
    background: 'none', border: 'none', color: '#9ca3af', fontSize: 12.5,
    cursor: 'pointer', textDecoration: 'underline', padding: 0,
  };

  return (
    <div style={{
      display: 'inline-flex', flexDirection: 'column', alignItems: 'stretch', gap: 10,
      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: 12, padding: '18px 22px', maxWidth: 380, width: '100%', textAlign: 'left',
    }}>
      <div style={{ fontSize: 13.5, color: '#e5e7eb', textAlign: 'center', lineHeight: 1.5 }}>
        {phase === 'entry'
          ? 'So we know who’s joining, what’s the email you registered with?'
          : 'We don’t see that email — is there another one you might use?'}
      </div>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourfirm.com" autoComplete="email" style={inputStyle} required />
        {suggestion && (
          <button type="button" onClick={() => setEmail(suggestion)}
            style={{ ...linkStyle, textAlign: 'left', color: '#F8A07A' }}>
            Did you mean {suggestion}?
          </button>
        )}
        <button type="submit" disabled={busy}
          style={{ ...joinBtnStyle, border: 'none', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? '…' : phase === 'entry' ? 'Continue' : 'Try this email'}
        </button>
      </form>
      {phase === 'retry' && (
        <button type="button" onClick={joinAsGuest} style={{ ...linkStyle, textAlign: 'center' }}>
          Join anyway
        </button>
      )}
      <button type="button" onClick={openZoom} style={{ ...linkStyle, textAlign: 'center', color: '#6b7280' }}>
        Having trouble? Just join
      </button>
    </div>
  );
}

interface DoorWhen { dayLong: string; timeShort: string; dateTimeFull: string }

interface DoorPayload {
  found: boolean;
  title?: string;
  when?: DoorWhen | null;
  scheduledAt?: string | null;
  durationMinutes?: number | null;
  captureActive?: boolean;
  zoom_ready?: boolean;
  doors_open?: boolean;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'fetch-error' }
  | { phase: 'not-found' }
  | { phase: 'ready'; door: DoorPayload };

type WindowPhase = 'before' | 'live' | 'after';

function windowPhase(scheduledAtIso: string | null | undefined, durationMinutes: number | null | undefined): WindowPhase {
  if (!scheduledAtIso) return 'before';
  const startMs = new Date(scheduledAtIso).getTime();
  if (!Number.isFinite(startMs)) return 'before';
  const doorOpenMs = startMs - DOOR_OPEN_MINUTES * 60_000;
  const endMs = startMs + (durationMinutes && durationMinutes > 0 ? durationMinutes : 60) * 60_000;
  const graceEndMs = endMs + GRACE_MINUTES_AFTER_END * 60_000;
  const now = Date.now();
  if (now < doorOpenMs) return 'before';
  if (now > graceEndMs) return 'after';
  return 'live';
}

function secondsUntilDoorOpen(scheduledAtIso: string): number {
  const doorMs = new Date(scheduledAtIso).getTime() - DOOR_OPEN_MINUTES * 60_000;
  return Math.floor((doorMs - Date.now()) / 1000);
}

function formatCountdown(secs: number): string {
  if (secs <= 0) return '';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (d > 0) return `${d}d ${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (h > 0) return `${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

function useDoorClock(scheduledAtIso: string | null | undefined, durationMinutes: number | null | undefined) {
  const [, setTick] = useState(0);
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!scheduledAtIso) return;
    ref.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [scheduledAtIso]);

  const phase = windowPhase(scheduledAtIso, durationMinutes);
  const countdownSecs = scheduledAtIso ? secondsUntilDoorOpen(scheduledAtIso) : -1;
  return { phase, countdownSecs };
}

export default function SessionDoorPage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [identity, setIdentity] = useState<DoorIdentity | null>(null);
  const [notMe, setNotMe] = useState(false);

  const handleRecognized = (idn: DoorIdentity) => {
    setNotMe(false);
    setIdentity(idn);
    try { localStorage.setItem(`session-checkin-id:${id}`, JSON.stringify(idn)); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!id) { setState({ phase: 'fetch-error' }); return; }
    fetch(`${API_BASE}/api/public/sessions/${encodeURIComponent(id)}/door`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`Server error ${r.status}`);
        const d = await r.json() as DoorPayload;
        setState(d.found ? { phase: 'ready', door: d } : { phase: 'not-found' });
      })
      .catch(() => setState({ phase: 'fetch-error' }));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    try {
      const remembered = localStorage.getItem(`session-checkin-id:${id}`);
      if (remembered) setIdentity(JSON.parse(remembered));
    } catch { /* ignore */ }
    const at = new URLSearchParams(window.location.search).get('at');
    if (!at) return;
    fetch(`${API_BASE}/api/public/sessions/${encodeURIComponent(id)}/identity?at=${encodeURIComponent(at)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.identity) setIdentity(d.identity); })
      .catch(() => { /* fail-open: stay anonymous */ });
  }, [id]);

  const recordCheckin = () => {
    if (!id) return;
    try { localStorage.setItem(`session-checkin-id:${id}`, JSON.stringify(identity)); } catch { /* ignore */ }
    const at = new URLSearchParams(window.location.search).get('at');
    if (!at) return;
    fetch(`${API_BASE}/api/public/sessions/${encodeURIComponent(id)}/checkin`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ at }), keepalive: true,
    }).catch(() => { /* the gate records; it never blocks entry */ });
  };

  const door = state.phase === 'ready' ? state.door : null;
  const { phase: windowState, countdownSecs } = useDoorClock(door?.scheduledAt, door?.durationMinutes);
  const joinUrl = id ? `${GET_HOST}/zoom/sessions/${encodeURIComponent(id)}` : null;

  if (state.phase === 'fetch-error') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center' }}>
          <div style={{ color: '#6b7280', fontSize: 14 }}>Having trouble loading this page.</div>
          {joinUrl && (
            <a href={joinUrl} target="_blank" rel="noopener noreferrer"
              style={{ display: 'inline-block', background: '#F05A28', color: '#fff', padding: '14px 32px', borderRadius: 8, fontSize: 15, fontWeight: 700, textDecoration: 'none' }}>
              Join the session
            </a>
          )}
        </div>
      </TakerShell>
    );
  }

  if (state.phase === 'loading') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#6b7280', fontSize: 14 }}>Loading…</div>
        </div>
      </TakerShell>
    );
  }

  if (state.phase === 'not-found') {
    return (
      <TakerShell>
        <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: '#991b1b', fontSize: 15 }}>This session was not found.</div>
        </div>
      </TakerShell>
    );
  }

  const title = door?.title ?? 'Your session';
  const when = door?.when ?? null;

  const joinBtnStyle: CSSProperties = {
    display: 'inline-block', background: '#F05A28', color: '#fff',
    padding: '14px 36px', borderRadius: 8, fontSize: 15, fontWeight: 700,
    textDecoration: 'none', letterSpacing: 0.2,
  };
  const cardStyle: CSSProperties = {
    display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10,
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 12, padding: '18px 28px', maxWidth: 380,
  };

  const bodySlot = (() => {
    if (windowState === 'after') {
      return (
        <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', maxWidth: 380 }}>
          This session has already taken place. Check your email for recap materials and what's next.
        </div>
      );
    }

    if (windowState === 'before') {
      return (
        <div style={{
          display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
          borderRadius: 12, padding: '16px 32px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#9ca3af' }}>
            Doors open in
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#F8A07A', fontVariantNumeric: 'tabular-nums', letterSpacing: 0.5 }}>
            {formatCountdown(countdownSecs)}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {DOOR_OPEN_MINUTES} minutes before the session starts
          </div>
        </div>
      );
    }

    // windowState === 'live'
    if (!door?.zoom_ready || !joinUrl) {
      return (
        <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center' }}>
          The join link hasn't been set up yet — check your reminder email, or check back shortly.
        </div>
      );
    }

    if (identity && !notMe) {
      const who = identity.firstName?.trim() || identity.email;
      return (
        <div style={cardStyle}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const, color: '#9ca3af' }}>
            You're joining as
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{who}</div>
          <a href={joinUrl} target="_blank" rel="noopener noreferrer"
            className="cta-btn" onClick={recordCheckin} style={joinBtnStyle}>
            Yes — enter the session
          </a>
          <button type="button" onClick={() => setNotMe(true)}
            style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
            Not you?
          </button>
        </div>
      );
    }

    return (
      <SessionDoorEmailCard
        sessionId={id ?? ''}
        joinUrl={joinUrl}
        joinBtnStyle={joinBtnStyle}
        onRecognized={handleRecognized}
      />
    );
  })();

  return (
    <TakerShell
      ctaHref={windowState === 'live' ? joinUrl : null}
      ctaLabel="Join the Session"
      footerLabel="Program session"
    >
      <section style={{
        background: 'linear-gradient(160deg, #0A0A2E 0%, #14143d 100%)',
        color: '#fff', padding: '72px 32px 88px', textAlign: 'center',
        fontFamily: "'Poppins', sans-serif", minHeight: '70vh',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 28,
      }}>
        <div>
          <h1 style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 'clamp(28px, 4.5vw, 40px)', fontWeight: 900, lineHeight: 1.15,
            marginBottom: 12, maxWidth: 640, margin: '0 auto 12px',
          }}>
            {title}
          </h1>
          {when && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 10, padding: '10px 20px', fontSize: 14, color: '#e2e8f0',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: windowState === 'after' ? '#6b7280' : '#4ade80', flexShrink: 0, boxShadow: windowState === 'after' ? 'none' : '0 0 6px #4ade80' }} />
              <span>{when.dateTimeFull}</span>
            </div>
          )}
        </div>
        {bodySlot}
      </section>
    </TakerShell>
  );
}
